import { isEqual } from 'lodash'

import { convertDateTimeToPreferedTimezone } from 'data/dateTime/convertDateTimeToPreferedTimezone'
import { formatDateToFEFormatDateFns } from 'utils/date'

type UserRef = {
  firstName?: string
  lastName?: string
  username?: string
}

/** One Envers-style revision row: audit fields plus the entity's own columns. */
export type RevisionLike = {
  rev?: number
  revType?: string
  type?: string
  createdAt?: string
  updatedAt?: string
  createdByUser?: UserRef
  updatedByUser?: UserRef
  lastModifiedBy?: string
  [entityField: string]: unknown
}

export type UserMeta = {
  modifiedBy: string
  modifiedByName: string
  modifiedByUsername: string
  modifiedAt: string
  epochMs: number
}

export type ChangeRow = UserMeta & {
  field: string
  fieldKey: string
  oldValue?: unknown
  newValue?: unknown
  changeType: string
  rev: number | string
  // Renders the value as-is instead of wrapping it in quotes (ActivityCell).
  renderRawValue?: boolean
}

export type ActivityRow = Partial<ChangeRow> & {
  id: string
  rev: number | string
  changeType: string
}

/** A field's row label, or a resolver for columns whose meaning depends on the revision around them. */
export type FieldLabel = string | ((current: RevisionLike, previous: RevisionLike | null) => string)

export type AddChangesConfig = {
  entityName?: string
  fieldConfig?: Record<string, FieldLabel>
}

type RevisionGroup = {
  parentMeta: UserMeta
  children: Array<Record<string, unknown>>
  ts: number
}

type RevisionKey = number | string

export type CustomBuilderFn = (builder: ActivityTreeBuilder, payload?: unknown) => void

export type TreePostProcessor = {
  run: (groups: Map<RevisionKey, RevisionGroup>, payload?: unknown) => void
  payload?: unknown
}

export const isEmpty = (value: unknown): boolean => value === null || value === undefined || value === ''

const tryConvertToPreferredTimezone = (isoDateTime: string): string => {
  try {
    const { onlyDate, onlyTime } = convertDateTimeToPreferedTimezone({ dateTime: isoDateTime })
    return [onlyDate, onlyTime].filter(Boolean).join(' | ')
  } catch {
    return ''
  }
}

/** Formats an ISO timestamp as "date | time" in the user's preferred timezone, degrading to the raw value. */
export const formatDateTime = (isoDateTime?: string | null): string => {
  if (!isoDateTime) return ''
  const timezoned = tryConvertToPreferredTimezone(isoDateTime)
  if (timezoned) return timezoned
  try {
    return formatDateToFEFormatDateFns(new Date(isoDateTime))
  } catch {
    return String(isoDateTime)
  }
}

/** Resolves who/when for a revision (updatedByUser/createdByUser + updatedAt/createdAt). */
export const extractUserMeta = (version?: RevisionLike | null): UserMeta => {
  // A modifier without a CRM user record (system identities like deactivations@haulerhero, or a
  // deleted user) must not fall back to createdByUser — that credits system changes to whoever
  // created the entity. Show the raw lastModifiedBy identity instead.
  const rawModifiedBy = version?.lastModifiedBy || ''
  const modifier = version?.updatedByUser || (rawModifiedBy ? {} : version?.createdByUser) || {}
  const name =
    [modifier.firstName, modifier.lastName].filter(Boolean).join(' ').trim() || modifier.username || rawModifiedBy || 'Unknown User'
  const timestampSource = version?.updatedAt || version?.createdAt || ''
  const parsedEpochMs = timestampSource ? new Date(timestampSource).getTime() : 0
  return {
    modifiedBy: name,
    modifiedByName: name,
    modifiedByUsername: modifier.username || '',
    modifiedAt: formatDateTime(timestampSource),
    epochMs: Number.isNaN(parsedEpochMs) ? 0 : parsedEpochMs,
  }
}

export const toChangeTypeFromValues = (previousValue: unknown, currentValue: unknown): string => {
  const previousEmpty = isEmpty(previousValue)
  const currentEmpty = isEmpty(currentValue)
  if (previousEmpty && !currentEmpty) return 'Added'
  if (!previousEmpty && currentEmpty) return 'Deleted'
  return 'Updated'
}

const resolveFieldLabel = (label: FieldLabel, current: RevisionLike, previous: RevisionLike | null): string =>
  typeof label === 'function' ? label(current, previous) : label

export default class ActivityTreeBuilder {
  private readonly groups = new Map<RevisionKey, RevisionGroup>()

  private readonly customBuilders = new Map<string, CustomBuilderFn>()

  private ensureGroup(rev: RevisionKey, version?: RevisionLike | null): RevisionGroup {
    const meta = extractUserMeta(version)
    const existingGroup = this.groups.get(rev)
    if (!existingGroup) {
      const createdGroup: RevisionGroup = { parentMeta: meta, children: [], ts: meta.epochMs || 0 }
      this.groups.set(rev, createdGroup)
      return createdGroup
    }
    // Always keep the metadata from the LATEST version in a revision batch
    if ((meta.epochMs || 0) > (existingGroup.ts || 0)) {
      existingGroup.parentMeta = meta
      existingGroup.ts = meta.epochMs || 0
    }
    return existingGroup
  }

  /** Generic field-based diffing for simple entities: one child row per configured field that changed. */
  addChanges(revisions: RevisionLike[] = [], config: AddChangesConfig = {}): this {
    const { entityName = '', fieldConfig = {} } = config || {}
    if (!Array.isArray(revisions) || revisions.length === 0) return this
    const fieldsEntries = Object.entries(fieldConfig)
    if (fieldsEntries.length === 0) return this

    const sorted = [...revisions].sort((a, b) => Number(a?.rev ?? 0) - Number(b?.rev ?? 0))

    sorted.forEach((revision, index) => {
      const current = revision || {}
      const prev = sorted[index - 1] || null
      const rev = current?.rev ?? index
      const revType = String(current?.revType || current?.type || '').toUpperCase()

      const group = this.ensureGroup(rev, current)
      const parentMeta = group.parentMeta || {}

      if (revType === 'DELETE') {
        // Envers DELETE revisions carry the full deleted entity — surface its identifying
        // fields so the entry says WHICH one was removed, not just that something was.
        let hasFieldDetail = false
        fieldsEntries.forEach(([key, label]) => {
          const oldValue = current?.[key]
          if (isEmpty(oldValue)) return
          hasFieldDetail = true
          const resolvedLabel = resolveFieldLabel(label, current, null)
          group.children.push({
            field: entityName ? `${entityName} - ${resolvedLabel}` : resolvedLabel,
            fieldKey: key,
            oldValue,
            newValue: undefined,
            changeType: 'Deleted',
            rev,
            ...parentMeta,
          })
        })
        if (!hasFieldDetail) {
          group.children.push({
            field: entityName || 'Entity',
            fieldKey: '__entity__',
            oldValue: undefined,
            newValue: undefined,
            changeType: 'Deleted',
            rev,
            ...parentMeta,
          })
        }
        return
      }

      // Oldest revision is an UPDATE with no INSERT baseline (record predates auditing) —
      // no prior state to diff against, so show one neutral marker instead of dumping every field.
      if (!prev && revType === 'UPDATE') {
        group.children.push({
          field: entityName ? `${entityName} — earliest recorded change` : 'Earliest recorded change',
          fieldKey: '__baseline__',
          oldValue: undefined,
          newValue: undefined,
          changeType: 'Baseline',
          rev,
          ...parentMeta,
        })
        return
      }

      const base = revType === 'INSERT' || !prev ? {} : prev

      fieldsEntries.forEach(([key, label]) => {
        const oldValue = base?.[key]
        const newValue = current?.[key]

        // Skip equals and None->None
        // Deep, not ===: the whitelist cannot promise these values are scalar.
        if (isEqual(oldValue, newValue)) return
        if (isEmpty(oldValue) && isEmpty(newValue)) return

        const changeType = toChangeTypeFromValues(oldValue, newValue)
        const resolvedLabel = resolveFieldLabel(label, current, prev)
        group.children.push({
          field: entityName ? `${entityName} - ${resolvedLabel}` : resolvedLabel,
          fieldKey: key,
          oldValue,
          newValue,
          changeType,
          rev,
          ...parentMeta,
        })
      })
    })
    return this
  }

  /** Escape hatch for custom builders to inject pre-formatted change rows. */
  addCustomChange(rev: RevisionKey, version: RevisionLike, changeObject: Record<string, unknown>): this {
    if (!rev || !version || !changeObject) return this
    const group = this.ensureGroup(rev, version)
    group.children.push({
      ...changeObject,
      rev,
      ...(group.parentMeta || {}),
    })
    return this
  }

  registerCustomBuilder(name: string, builderFn: CustomBuilderFn): this {
    if (name && typeof builderFn === 'function') {
      this.customBuilders.set(name, builderFn)
    }
    return this
  }

  runCustomBuilder(name: string, payload?: unknown): this {
    const builderFn = this.customBuilders.get(name)
    if (typeof builderFn === 'function') {
      builderFn(this, payload)
    }
    return this
  }

  /** Builds the final DataGrid-ready rows: one batch row per revision, newest first, children sorted by field. */
  buildTree(postProcessors: TreePostProcessor[] = []): ActivityRow[] {
    postProcessors.forEach(processor => {
      if (processor && typeof processor.run === 'function') {
        processor.run(this.groups, processor.payload)
      }
    })

    // Order batches by recorded time (newest first). Envers feeds keep their existing rev-desc
    // order (rev is monotonic with time); the route-slot feed lives in a different rev space, so
    // occurredAt is the only correct key to interleave the two sources. Ties break by numeric rev,
    // falling back to a stable key compare so mixed-space ties stay deterministic.
    const revisionKeys = Array.from(this.groups.keys()).sort((keyA, keyB) => {
      const tsDelta = (this.groups.get(keyB)?.ts ?? 0) - (this.groups.get(keyA)?.ts ?? 0)
      if (tsDelta !== 0) return tsDelta
      const numA = Number(keyA)
      const numB = Number(keyB)
      if (Number.isFinite(numA) && Number.isFinite(numB)) return numB - numA
      return String(keyB).localeCompare(String(keyA))
    })
    const rows: ActivityRow[] = []

    // One row per change, not a group row plus children: each change already carries its group's
    // who and when, so the batch row only cost a click to get past.
    revisionKeys.forEach(rev => {
      const group = this.groups.get(rev)
      if (!group || !group.children || group.children.length === 0) return

      const sortedChildren = [...group.children].sort((a, b) =>
        String(a.field || '')
          .toLocaleLowerCase()
          .localeCompare(String(b.field || '').toLocaleLowerCase())
      )

      sortedChildren.forEach((child, childIndex) => {
        rows.push({ ...child, id: `rev-${rev}-change-${childIndex}` } as ActivityRow)
      })
    })
    return rows
  }
}
